import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '@environments/environment';

export interface ConfiguracaoAvaliacao {
  PARECER?: { visivel: boolean };
  COMPETENCIA?: { visivel: boolean };
  CONHECIMENTO?: { visivel: boolean };
  HABILIDADE?: { visivel: boolean };
  ATITUDE?: { visivel: boolean };
}

export interface ItemAvaliavel {
  id: number;
  descricao: string;
  tipoPeriodo: string;
  modoAvaliacao: string;
  data?: string;
  notaMinima?: number;
  notaMaxima?: number;
  identificadorExameFinal?: string;
  publicavel?: {
    publicado?: boolean;
    dataPublicacao?: string | null;
  };
  publicado?: boolean;
  dataPublicacao?: string | null;
  itemEducacional?: {
    id: number;
    descricao: string;
  };
  sigla?: string;
  periodoAvaliativo?: {
    id: number;
    descricao?: string;
    dataInicial?: string;
    dataFinal?: string;
  };
  idPeriodo?: number;
}

export interface RegistroAvaliacao {
  id?: number;
  notaNumerica?: number;
  notaDescritiva?: string;
  notaConceito?: string | { sigla?: string; descricao?: string; [key: string]: unknown };
  parecer?: string;
  parecerDetalhadoConteudo?: string;
  avaliavel: ItemAvaliavel;
  enturmacao: {
    id: number;
    avaliacaoDescritiva?: boolean;
    matricula: {
      id: number;
      nome: string | null;
      nomeSocial?: string | null;
      situacao: string | null;
      avaliacaoDescritiva?: boolean;
    };
    numeroChamada: number;
    situacao: string | null;
    dataFim?: string;
  };
  $$editavel?: boolean;
  $$isSaving?: boolean;
  $$semNota?: boolean;
  $$permiteParecer?: boolean;
  $$permiteParecerAnual?: boolean;
  $$permiteCha?: boolean;
  hasNotaFilhosPreenchido?: boolean;
  updateFields?: () => void;
}

export interface FormulaCalculo {
  id: number;
  descricao: string;
  estabelecimento?: {
    id: number;
    nome: string;
  };
}

export interface CalculoFormula {
  id: number;
  status: 'PROCESSANDO' | 'CONCLUIDO' | 'ERRO';
  dataProcessamento?: string;
  alterado?: boolean;
  formulaCalculo?: FormulaCalculo;
}

@Injectable({
  providedIn: 'root'
})
export class AvaliacaoService {
  private configuracaoCache = new Map<number, Observable<ConfiguracaoAvaliacao>>();
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  getConfiguracaoAvaliacao(turmaId: number): Observable<ConfiguracaoAvaliacao> {
    if (!turmaId || turmaId <= 0) {
      return of({});
    }

    if (!this.configuracaoCache.has(turmaId)) {
      const request = this.http.get<ConfiguracaoAvaliacao>(
        `${this.apiUrl}/commons/turma/${turmaId}/configuracao-avaliacoes`
      ).pipe(
        shareReplay(1)
      );
      this.configuracaoCache.set(turmaId, request);
    }

    return this.configuracaoCache.get(turmaId)!;
  }

  /**
   * Nada neste app chama este método hoje — mantido de propósito, não é dead code.
   * O app só lê configuração de avaliação (quem edita é a secretaria, em outro
   * sistema); cache em memória já é limpo por um reload. Só importaria se a
   * secretaria editasse a config de uma turma que o professor já tem aberta.
   */
  clearConfiguracaoCache(turmaId?: number): void {
    if (turmaId) {
      this.configuracaoCache.delete(turmaId);
    } else {
      this.configuracaoCache.clear();
    }
  }

  listAvaliacoes(turmaId: number, periodoId?: number, itemEducacionalId?: number, filter?: string): Observable<{
    itensAvaliaveis: ItemAvaliavel[];
    registrosAvaliacao: RegistroAvaliacao[];
    registros?: RegistroAvaliacao[]; // Fallback para compatibilidade
  }> {
    // Só adiciona cada parâmetro se tiver valor, igual ao AngularJS
    let params = new HttpParams().set('turma', turmaId.toString());

    if (periodoId !== null && periodoId !== undefined) {
      params = params.set('periodoAvaliativo', periodoId.toString());
    }

    if (itemEducacionalId !== null && itemEducacionalId !== undefined) {
      params = params.set('itemEducacional', itemEducacionalId.toString());
    }

    if (filter !== null && filter !== undefined && filter.trim().length > 0) {
      const sanitizedFilter = filter.trim().substring(0, 500); // limita tamanho
      params = params.set('filter', sanitizedFilter);
    }

    return this.http.get<{
      itensAvaliaveis: ItemAvaliavel[];
      registrosAvaliacao: RegistroAvaliacao[];
      registros?: RegistroAvaliacao[];
    }>(`${this.apiUrl}/commons/instrumento-avaliacao`, { params }).pipe(
      map(response => {
        if (!response) {
          return { itensAvaliaveis: [], registrosAvaliacao: [] };
        }
        // `registros` é alias de `registrosAvaliacao` para compatibilidade
        return {
          itensAvaliaveis: response.itensAvaliaveis || [],
          registrosAvaliacao: response.registrosAvaliacao || response.registros || [],
          registros: response.registrosAvaliacao || response.registros || []
        };
      })
    );
  }

  salvar(turmaId: number, nota: RegistroAvaliacao, context?: HttpContext): Observable<RegistroAvaliacao> {
    const url = `${this.apiUrl}/commons/turma/${turmaId}/avaliacoes`;
    const options = context ? { context } : {};
    if (nota.id) {
      return this.http.put<RegistroAvaliacao>(`${url}/${nota.id}`, nota, options);
    } else {
      return this.http.post<RegistroAvaliacao>(url, nota, options);
    }
  }

  publicarItemAvaliavel(turmaId: number, itemAvaliavelId: number): Observable<ItemAvaliavel> {
    return this.http.put<ItemAvaliavel>(
      `${this.apiUrl}/commons/turma/${turmaId}/avaliacoes/publicar/${itemAvaliavelId}`,
      {}
    );
  }

  getFormulasCalculo(filter?: string, offset = 0, limit = 999): Observable<{
    content: FormulaCalculo[];
    totalElements: number;
  }> {
    const params = new HttpParams()
      .set('filter', filter || '')
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    return this.http.get<{
      content: FormulaCalculo[];
      totalElements: number;
    }>(`${this.apiUrl}/commons/formula-calculo/formulas-instrumentos`, { params });
  }

  calcularFormula(turmaId: number, itemEducacionalId: number, formulaId: number, periodoId?: number): Observable<any> {
    const payload = {
      turmaId,
      itemEducacionalId,
      periodoAvaliativoId: periodoId,
      formulaId
    };

    return this.http.post(`${this.apiUrl}/commons/instrumento-avaliacao/calcular`, payload);
  }

  statusCalculo(turmaId: number, itemEducacionalId: number, periodoId?: number): Observable<{
    calculo: CalculoFormula;
  }> {
    const params = new HttpParams()
      .set('turmaId', turmaId.toString())
      .set('periodoAvaliativoId', periodoId?.toString() || '')
      .set('itemEducacionalId', itemEducacionalId.toString());

    return this.http.get<{
      calculo: CalculoFormula;
    }>(`${this.apiUrl}/commons/instrumento-avaliacao/status-calculo`, { params });
  }

  removeCalculoInstrumento(turmaId: number, periodoId: number, disciplinaId: number): Observable<any> {
    const params = new HttpParams()
      .set('idPeriodo', periodoId.toString())
      .set('idTurma', turmaId.toString())
      .set('idDisciplina', disciplinaId.toString());

    return this.http.delete(`${this.apiUrl}/professores/calculo-instrumento`, { params });
  }

  listItensAvaliaveis(turmaId: number, filter?: string): Observable<{
    content: ItemAvaliavel[];
  }> {
    const params = new HttpParams()
      .set('filter', filter || '')
      .set('limit', '999');

    return this.http.get<{
      content: ItemAvaliavel[];
    }>(`${this.apiUrl}/commons/turma/${turmaId}/configuracao-avaliacoes/itens-avaliaveis`, { params });
  }

  listAvaliacoesCompletos(turmaId: number, filter?: string): Observable<{
    content: RegistroAvaliacao[];
  }> {
    const params = new HttpParams()
      .set('filter', filter || '')
      .set('limit', '999');

    return this.http.get<{
      content: RegistroAvaliacao[];
    }>(`${this.apiUrl}/commons/turma/${turmaId}/avaliacoes/completos`, { params });
  }

  getDesempenhoComParecerDetalhado(registroAvaliacaoId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/commons/avaliacoes/${registroAvaliacaoId}`);
  }

  // Métodos para visualização anual
  getObjetivosAprendizagem(turma: { id: number }, periodo: { id: number }, componente: { id: number }): Observable<Array<{ id?: number; descricao?: string }>> {
    return this.http.get<Array<{ id?: number; descricao?: string }>>(`${this.apiUrl}/commons/avaliacoes/objetivos-aprendizagem`, {
      params: {
        turmaId: turma.id,
        periodoId: periodo.id,
        componenteId: componente.id
      }
    });
  }

  getAnexosDesempenho(turma: { id: number }, periodo: { id: number }, componente: { id: number }): Observable<Array<{ id?: number; nome?: string; url?: string }>> {
    return this.http.get<Array<{ id?: number; nome?: string; url?: string }>>(`${this.apiUrl}/commons/avaliacoes/anexos`, {
      params: {
        turmaId: turma.id.toString(),
        periodoId: periodo.id.toString(),
        componenteId: componente.id.toString()
      }
    });
  }

  getEnturmacoesHistoricas(turmaId: number, filter?: string, offset = 0, limit = 999): Observable<any[]> {
    let params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    if (filter) {
      params = params.set('filter', filter);
    }

    return this.http.get<any[]>(`${this.apiUrl}/commons/turma/${turmaId}/enturmacoes-historicas`, { params });
  }

  getEncerramentosByTurma(turmaId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/commons/encerramento/encerramentos-turma/${turmaId}`);
  }

  getItensEducacionais(turmaId: number): Observable<ItemAvaliavel[]> {
    return this.http.get<ItemAvaliavel[]>(`${this.apiUrl}/commons/turma/${turmaId}/itens-educacionais`);
  }

  getPeriodosAvaliativos(turmaId: number): Observable<any[]> {
    const params = new HttpParams().set('turma', turmaId.toString());
    return this.http.get<any[]>(`${this.apiUrl}/commons/periodos-avaliativos`, { params });
  }

  salvarDataExameConselho(turmaId: number, itemEducacionalId: number, item: any): Observable<any> {
    const url = `${this.apiUrl}/commons/turma/${turmaId}/avaliacoes/data-exame-conselho`;
    
    const payload = {
      data: item.data,
      tipoPeriodo: item.tipoPeriodo,
      identificadorExameFinal: item.notaRef?.avaliavel?.identificador || null,
      itemEducacional: itemEducacionalId
    };

    return this.http.post(url, payload);
  }
}